import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AuthProvider, useAuth } from './context/AuthContext';

// Layout
import AppLayout from './components/layout/AppLayout';

// Auth Pages
import LoginPage from './pages/auth/LoginPage';
import RegisterPage from './pages/auth/RegisterPage';
import ForgotPasswordPage from './pages/auth/ForgotPasswordPage';

// Dashboard
import DashboardPage from './pages/dashboard/DashboardPage';
import SuperAdminDashboard from './pages/dashboard/SuperAdminDashboard';

// Properties
import PropertiesPage from './pages/properties/PropertiesPage';
import PropertyDetailPage from './pages/properties/PropertyDetailPage';
import PropertyFormPage from './pages/properties/PropertyFormPage';

// Bookings
import BookingsPage from './pages/bookings/BookingsPage';
import BookingDetailPage from './pages/bookings/BookingDetailPage';

// Equipment
import EquipmentPage from './pages/equipment/EquipmentPage';
import EquipmentDetailPage from './pages/equipment/EquipmentDetailPage';
import EquipmentFormPage from './pages/equipment/EquipmentFormPage';
import EquipmentBookingsPage from './pages/equipment/EquipmentBookingsPage';
import EquipmentOpsDashboard from './pages/equipment/EquipmentOpsDashboard';
import EquipmentBookingFormPage from './pages/equipment/EquipmentBookingFormPage';

// CRM
import CRMPage from './pages/crm/CRMPage';
import LeadDetailPage from './pages/crm/LeadDetailPage';
import LeadFormPage from './pages/crm/LeadFormPage';

// Maintenance
import MaintenancePage from './pages/maintenance/MaintenancePage';

// Reports
import ReportsPage from './pages/reports/ReportsPage';

// Content
import ContentPage from './pages/content/ContentPage';

// Users
import UsersPage from './pages/users/UsersPage';

// Guest Portal
import GuestDashboard from './pages/guest/GuestDashboard';
import GuestBookings from './pages/guest/GuestBookings';
import GuestCheckIn from './pages/guest/GuestCheckIn';

// Billing
import BillingPage from './pages/billing/BillingPage';

// Settings
import SettingsPage from './pages/settings/SettingsPage';
import ProfilePage from './pages/settings/ProfilePage';

// Super Admin
import TenantsPage from './pages/superadmin/TenantsPage';
import TenantDetailPage from './pages/superadmin/TenantDetailPage';
import ChannelManagerAdminPage from './pages/superadmin/ChannelManagerAdminPage';

// Channel Manager
import ChannelManagerPage from './pages/channelManager/ChannelManagerPage';

// Booking Pipeline
import BookingPipelinePage from './pages/bookings/BookingPipelinePage';

// Calendar
import CalendarPage from './pages/calendar/CalendarPage';

const ProtectedRoute = ({ children, roles }) => {
  const { user, loading } = useAuth();
  if (loading) return (
    <div className="loading-center">
      <div className="spinner spinner-lg" />
    </div>
  );
  if (!user) return <Navigate to="/login" replace />;
  if (roles && !roles.includes(user.role)) return <Navigate to="/dashboard" replace />;
  return children;
};

const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (user) {
    if (user.role === 'superadmin') return <Navigate to="/super-admin" replace />;
    if (user.role === 'guest') return <Navigate to="/guest/dashboard" replace />;
    return <Navigate to="/dashboard" replace />;
  }
  return children;
};

const AppRoutes = () => {
  const { user } = useAuth();

  return (
    <Routes>
      {/* Public */}
      <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />
      <Route path="/register" element={<PublicRoute><RegisterPage /></PublicRoute>} />
      <Route path="/forgot-password" element={<PublicRoute><ForgotPasswordPage /></PublicRoute>} />

      {/* Super Admin */}
      <Route path="/super-admin" element={
        <ProtectedRoute roles={['superadmin']}>
          <AppLayout><SuperAdminDashboard /></AppLayout>
        </ProtectedRoute>
      } />
      <Route path="/super-admin/tenants" element={
        <ProtectedRoute roles={['superadmin']}>
          <AppLayout><TenantsPage /></AppLayout>
        </ProtectedRoute>
      } />
      <Route path="/super-admin/tenants/:id" element={
        <ProtectedRoute roles={['superadmin']}>
          <AppLayout><TenantDetailPage /></AppLayout>
        </ProtectedRoute>
      } />
      <Route path="/super-admin/channel-manager" element={
        <ProtectedRoute roles={['superadmin']}>
          <AppLayout><ChannelManagerAdminPage /></AppLayout>
        </ProtectedRoute>
      } />

      {/* Channel Manager */}
      <Route path="/channel-manager" element={
        <ProtectedRoute roles={['superadmin', 'tenant_admin', 'owner', 'manager']}>
          <AppLayout><ChannelManagerPage /></AppLayout>
        </ProtectedRoute>
      } />

      {/* Main App */}
      <Route path="/dashboard" element={
        <ProtectedRoute roles={['superadmin', 'tenant_admin', 'owner', 'manager', 'staff']}>
          <AppLayout><DashboardPage /></AppLayout>
        </ProtectedRoute>
      } />

      {/* Properties */}
      <Route path="/properties" element={
        <ProtectedRoute>
          <AppLayout><PropertiesPage /></AppLayout>
        </ProtectedRoute>
      } />
      <Route path="/properties/new" element={
        <ProtectedRoute roles={['superadmin', 'tenant_admin', 'owner']}>
          <AppLayout><PropertyFormPage /></AppLayout>
        </ProtectedRoute>
      } />
      <Route path="/properties/:id" element={
        <ProtectedRoute>
          <AppLayout><PropertyDetailPage /></AppLayout>
        </ProtectedRoute>
      } />
      <Route path="/properties/:id/edit" element={
        <ProtectedRoute roles={['superadmin', 'tenant_admin', 'owner', 'manager']}>
          <AppLayout><PropertyFormPage /></AppLayout>
        </ProtectedRoute>
      } />

      {/* Bookings */}
      <Route path="/bookings/pipeline" element={
        <ProtectedRoute roles={['superadmin', 'tenant_admin', 'owner', 'manager', 'staff']}>
          <AppLayout><BookingPipelinePage /></AppLayout>
        </ProtectedRoute>
      } />
      <Route path="/bookings/new" element={
        <ProtectedRoute roles={['superadmin', 'tenant_admin', 'owner', 'manager', 'staff']}>
          <AppLayout><BookingDetailPage isNew={true} /></AppLayout>
        </ProtectedRoute>
      } />
      <Route path="/bookings" element={
        <ProtectedRoute>
          <AppLayout><BookingsPage /></AppLayout>
        </ProtectedRoute>
      } />
      <Route path="/bookings/:id" element={
        <ProtectedRoute>
          <AppLayout><BookingDetailPage /></AppLayout>
        </ProtectedRoute>
      } />
      <Route path="/calendar" element={
        <ProtectedRoute>
          <AppLayout><CalendarPage /></AppLayout>
        </ProtectedRoute>
      } />

      {/* Equipment */}
      <Route path="/equipment" element={
        <ProtectedRoute>
          <AppLayout><EquipmentPage /></AppLayout>
        </ProtectedRoute>
      } />
      <Route path="/equipment/new" element={
        <ProtectedRoute roles={['superadmin', 'tenant_admin', 'owner', 'manager']}>
          <AppLayout><EquipmentFormPage /></AppLayout>
        </ProtectedRoute>
      } />
      <Route path="/equipment/bookings" element={
        <ProtectedRoute>
          <AppLayout><EquipmentBookingsPage /></AppLayout>
        </ProtectedRoute>
      } />
      <Route path="/equipment/operations" element={
        <ProtectedRoute roles={['superadmin', 'tenant_admin', 'owner', 'manager', 'staff']}>
          <AppLayout><EquipmentOpsDashboard /></AppLayout>
        </ProtectedRoute>
      } />
      <Route path="/equipment/:id" element={
        <ProtectedRoute>
          <AppLayout><EquipmentDetailPage /></AppLayout>
        </ProtectedRoute>
      } />
      <Route path="/equipment/:id/book" element={
        <ProtectedRoute>
          <AppLayout><EquipmentBookingFormPage /></AppLayout>
        </ProtectedRoute>
      } />
      <Route path="/equipment/:id/edit" element={
        <ProtectedRoute roles={['superadmin', 'tenant_admin', 'owner', 'manager']}>
          <AppLayout><EquipmentFormPage /></AppLayout>
        </ProtectedRoute>
      } />

      {/* CRM */}
      <Route path="/crm" element={
        <ProtectedRoute roles={['superadmin', 'tenant_admin', 'owner', 'manager', 'staff']}>
          <AppLayout><CRMPage /></AppLayout>
        </ProtectedRoute>
      } />
      <Route path="/crm/new" element={
        <ProtectedRoute roles={['superadmin', 'tenant_admin', 'owner', 'manager', 'staff']}>
          <AppLayout><LeadFormPage /></AppLayout>
        </ProtectedRoute>
      } />
      <Route path="/crm/:id" element={
        <ProtectedRoute roles={['superadmin', 'tenant_admin', 'owner', 'manager', 'staff']}>
          <AppLayout><LeadDetailPage /></AppLayout>
        </ProtectedRoute>
      } />

      {/* Maintenance */}
      <Route path="/maintenance" element={
        <ProtectedRoute>
          <AppLayout><MaintenancePage /></AppLayout>
        </ProtectedRoute>
      } />

      {/* Reports */}
      <Route path="/reports" element={
        <ProtectedRoute roles={['superadmin', 'tenant_admin', 'owner', 'manager']}>
          <AppLayout><ReportsPage /></AppLayout>
        </ProtectedRoute>
      } />

      {/* Content */}
      <Route path="/content" element={
        <ProtectedRoute roles={['superadmin', 'tenant_admin', 'owner', 'manager']}>
          <AppLayout><ContentPage /></AppLayout>
        </ProtectedRoute>
      } />

      {/* Users */}
      <Route path="/users" element={
        <ProtectedRoute roles={['superadmin', 'tenant_admin', 'owner', 'manager']}>
          <AppLayout><UsersPage /></AppLayout>
        </ProtectedRoute>
      } />

      {/* Billing */}
      <Route path="/billing" element={
        <ProtectedRoute roles={['superadmin', 'tenant_admin']}>
          <AppLayout><BillingPage /></AppLayout>
        </ProtectedRoute>
      } />

      {/* Settings */}
      <Route path="/settings" element={
        <ProtectedRoute>
          <AppLayout><SettingsPage /></AppLayout>
        </ProtectedRoute>
      } />
      <Route path="/profile" element={
        <ProtectedRoute>
          <AppLayout><ProfilePage /></AppLayout>
        </ProtectedRoute>
      } />

      {/* Guest Portal */}
      <Route path="/guest/dashboard" element={
        <ProtectedRoute roles={['guest']}>
          <AppLayout isGuest><GuestDashboard /></AppLayout>
        </ProtectedRoute>
      } />
      <Route path="/guest/bookings" element={
        <ProtectedRoute roles={['guest']}>
          <AppLayout isGuest><GuestBookings /></AppLayout>
        </ProtectedRoute>
      } />
      <Route path="/guest/check-in/:bookingId" element={
        <ProtectedRoute roles={['guest']}>
          <AppLayout isGuest><GuestCheckIn /></AppLayout>
        </ProtectedRoute>
      } />

      {/* Root redirect */}
      <Route path="/" element={<Navigate to="/dashboard" replace />} />
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
};

const App = () => (
  <BrowserRouter>
    <AuthProvider>
      <AppRoutes />
      <Toaster
        position="top-right"
        toastOptions={{
          duration: 4000,
          style: { borderRadius: '10px', fontSize: '14px', fontFamily: 'Inter, sans-serif' }
        }}
      />
    </AuthProvider>
  </BrowserRouter>
);

export default App;
